import { Link, useLocation } from 'react-router-dom';

import { sideBarItem } from '@/components/Sidebar/constants';
import { SidebarContent, SidebarMenu, SidebarMenuItem } from '@/components/ui/sidebar';
import { MenuItem, MenuSection } from '@/types/menu';
import { useRootSelector } from '@/hooks/selector.hook';
import { getSidebarGroups } from '@/utils/sidebar';
import { resolveMenuIcon } from '@/components/Sidebar/api-icon-map';

const ROUTES_WITHOUT_ACTIVE_SIDEBAR = new Set(['/admin/profile']);

const normalizeRouteForMatch = (route: string) => {
  const safeRoute = route.startsWith('/') ? route : `/${route}`;
  return safeRoute === '/' ? safeRoute : safeRoute.replace(/\/+$/, '');
};

interface SidebarNavigationProps {
  isCollapsed: boolean;
  onItemClick?: () => void;
}

export default function SidebarNavigation({ isCollapsed, onItemClick }: SidebarNavigationProps) {
  const location = useLocation();
  const { dataSidebar } = useRootSelector((state) => state.sidebar);

  const getSafeRoute = (route: string) => {
    return route.startsWith('/') ? route : `/${route}`;
  };

  const currentPath = normalizeRouteForMatch(location.pathname);
  const apiGroups = getSidebarGroups(dataSidebar);
  const apiMenuSections: MenuSection[] = apiGroups.map((g) => ({
    label: g.label,
    items: g.items.map((it) => ({
      label: it.label,
      route: it.route || '',
      icon: resolveMenuIcon({ icon: typeof it.icon === 'string' ? it.icon : null, route: it.route }),
    })),
  }));

  const menuSections: MenuSection[] =
    apiMenuSections.length > 0 ? apiMenuSections : (sideBarItem as MenuSection[]);

  const menuItems = menuSections.flatMap((section) => section.items);

  const getActiveRoute = (items: MenuItem[]): string | null => {
    if (ROUTES_WITHOUT_ACTIVE_SIDEBAR.has(currentPath)) {
      return null;
    }

    let bestMatch: string | null = null;
    let maxLen = -1;

    items.forEach((item) => {
      const safeRoute = normalizeRouteForMatch(item.route);

      if (currentPath === safeRoute || currentPath.startsWith(`${safeRoute}/`)) {
        if (safeRoute.length > maxLen) {
          maxLen = safeRoute.length;
          bestMatch = item.route;
        }
      }
    });

    return bestMatch;
  };

  const activeRoute = getActiveRoute(menuItems);

  const isActive = (route: string) => {
    if (!activeRoute) return false;
    return normalizeRouteForMatch(route) === normalizeRouteForMatch(activeRoute);
  };

  return (
    <SidebarContent
      className={
        isCollapsed
          ? 'mt-[2rem] !overflow-visible px-[1.2rem]'
          : 'mt-[2.4rem] px-[1.6rem] lg:mt-[4rem]'
      }
    >
      <SidebarMenu
        className={`flex flex-col text-white ${
          isCollapsed
            ? 'mt-[2rem] items-center gap-[2.8rem] !overflow-visible'
            : 'gap-[1.6rem] lg:mt-[1rem]'
        }`}
      >
        {menuSections.map((section: MenuSection) => (
          <div key={section.label} className="w-full">
            {!isCollapsed && (
              <p className="mb-[0.8rem] px-[0.2rem] text-[1.4rem] font-normal leading-[1.8rem] text-[#7988A4]">
                {section.label}
              </p>
            )}

            <div className={`flex flex-col ${isCollapsed ? 'gap-[1.2rem]' : 'gap-[0.4rem]'}`}>
              {section.items.map((item) => {
                const active = isActive(item.route);
                const Icon = item.icon;

                return (
                  <SidebarMenuItem
                    key={`${section.label}-${item.label}`}
                    className={`group/sidebar-menu-item relative transition-colors ${
                      isCollapsed
                        ? `h-[4.4rem] w-[4.4rem] rounded-[1rem] ${
                            active
                              ? 'bg-[#E9EFFD] text-[#2563EB] lg:bg-[#1C37E0] lg:text-white'
                              : 'bg-transparent text-[#7988A4] hover:bg-[#E9EFFD] hover:text-[#2563EB] hover:lg:bg-[#1C37E0] hover:lg:text-white'
                          }`
                        : `rounded-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-normal hover:bg-[#1C37E0] hover:text-white ${
                            active
                              ? 'bg-[#E9EFFD] text-[#2563EB] lg:bg-[#1C37E0] lg:text-white'
                              : 'bg-transparent text-white'
                          }`
                    }`}
                  >
                    <Link
                      to={getSafeRoute(item.route)}
                      onClick={onItemClick}
                      className={`flex h-full w-full cursor-pointer items-center bg-transparent text-left ${
                        isCollapsed ? 'justify-center' : 'gap-[1rem] px-[1.4rem] py-[1rem]'
                      }`}
                    >
                      <Icon
                        className={isCollapsed ? '!h-[1.6rem] !w-auto' : '!h-[1.8rem] !w-auto'}
                      />
                      {!isCollapsed && item.label}
                    </Link>

                    {isCollapsed && (
                      <span className="pointer-events-none invisible absolute left-[calc(100%_+_1rem)] top-1/2 z-50 flex h-[3.8rem] translate-x-[-0.6rem] translate-y-[-50%] items-center whitespace-nowrap rounded-[0.4rem] bg-[#3463FD] px-[1.25rem] py-[0.25rem] text-[1.4rem] font-semibold text-white opacity-0 shadow-[0_8px_20px_rgba(28,55,224,0.25)] transition-all duration-200 ease-out group-hover/sidebar-menu-item:visible group-hover/sidebar-menu-item:translate-x-0 group-hover/sidebar-menu-item:opacity-100">
                        {item.label}
                      </span>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </div>
          </div>
        ))}
      </SidebarMenu>
    </SidebarContent>
  );
}
